import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  DialogActions,
  Divider,
  useTheme,
  Switch,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { FormattedMessage } from "react-intl";
import dayjs from "dayjs";
import { CompaniesAccountFiltersType } from "../../../types/Filter";
import { useLocation } from "react-router-dom";

interface FiltersModalProps {
  open: boolean;
  handleClose: () => void;
  onApplyFilters: (filters: CompaniesAccountFiltersType) => void;
  onClearFilters: () => void;
  isClearFilters: boolean;
}

const FiltersModal = ({
  open,
  handleClose,
  onApplyFilters,
  isClearFilters,
}: FiltersModalProps) => {
  const location = useLocation();

  const getQueryParam = (param: string) => {
    const params = new URLSearchParams(location.search);
    return params.get(param) || "";
  };

  const [accountStatus, setAccountStatus] = useState<boolean>();
  const [accountLanguage, setAccountLanguage] = useState<string>();
  const [createdAtStart, setCreatedAtStart] = useState<string>();
  const [createdAtEnd, setCreatedAtEnd] = useState<string>();

  // Update state if query params change (e.g., modal reopened)
  useEffect(() => {
    const status = getQueryParam("account_status");
    const lang = getQueryParam("account_language");
    const start = getQueryParam("created_at_start");
    const end = getQueryParam("created_at_end");

    setAccountStatus(status === "true");
    setAccountLanguage(lang || "");
    setCreatedAtStart(start || "");
    setCreatedAtEnd(end || "");
  }, [location.search]);

  useEffect(() => {
    // When filtersResetKey changes, reset all fields to default values
    setAccountStatus(false); // or whatever your default is
    setAccountLanguage("");
    setCreatedAtStart("");
    setCreatedAtEnd("");
  }, [isClearFilters]);

  const handleApply = () => {
    const filters: CompaniesAccountFiltersType = {
      account_status: String(accountStatus),
      account_language: String(accountLanguage),
      created_at_start: String(createdAtStart),
      created_at_end: String(createdAtEnd),
    };
    onApplyFilters(filters);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: "1.3rem", fontWeight: 500 }}>
        <FormattedMessage id="Filters" />
      </DialogTitle>
      <Divider sx={{ marginInline: "1rem" }} />
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  sx={{ width: "100%" }}
                  value={createdAtStart ? dayjs(createdAtStart) : null}
                  onChange={(date) => {
                    setCreatedAtStart(date ? date.format("YYYY-MM-DD") : "");
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      label: <FormattedMessage id="Created At Start" />,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  sx={{ width: "100%" }}
                  value={createdAtEnd ? dayjs(createdAtEnd) : null}
                  onChange={(date) => {
                    setCreatedAtEnd(date ? date.format("YYYY-MM-DD") : "");
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      label: <FormattedMessage id="Created At End" />,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>
                  <FormattedMessage id="Account Language" />
                </InputLabel>
                <Select
                  value={accountLanguage || ""}
                  onChange={(e) => setAccountLanguage(e.target.value)}
                  label={<FormattedMessage id="Account Language" />}
                >
                  <MenuItem value="">
                    <FormattedMessage id="All" />
                  </MenuItem>
                  <MenuItem value="AR">
                    <FormattedMessage id="Arabic" />
                  </MenuItem>
                  <MenuItem value="EN">
                    <FormattedMessage id="English" />
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={accountStatus}
                    onChange={(e) => setAccountStatus(e.target.checked)}
                    color="primary"
                  />
                }
                label={<FormattedMessage id="Account Status" />}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "1rem" }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleClose}
          sx={{ padding: ".3rem 3rem " }}
        >
          <FormattedMessage id="Cancel" />
        </Button>

        <Button
          onClick={handleApply}
          variant="contained"
          color="primary"
          sx={{ padding: ".3rem 3rem " }}
        >
          <FormattedMessage id="Apply" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FiltersModal;
